import React, { useEffect, useState, useCallback, useMemo } from "react";
import NavbarWrapper from "../../../widgets/navbarWrapper";
import { Link, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../loading/spinner";
import prescriptionBlack from "../../../assets/prescriptionBlack.svg";
import upload from "../../../assets/upload.png";

// ====== Import all updated child components ======
import VerifyPatientModal from "./varifyPatientModal";
import EditScheduleAppointment from "./editScheduleAppointment";
import DeleteScheduleAppointment from "./deleteScheduleAppointment";
import PaymentMode from "./paymentMode";
import UnpaidtoPaid from "./dentistPayment/unpaidToPaid";
import SettleDuePayment from "./settleDuePayment";
import DueModal from "./dueModal";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getDoctorData = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

const getClinics = () => {
  try {
    return JSON.parse(localStorage.getItem("clinics")) || [];
  } catch {
    return [];
  }
};

const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const saveAppointments = (appointments) => {
  localStorage.setItem("appointments", JSON.stringify(appointments));
};

// ============================================
// MAIN COMPONENT
// ============================================

const ListOfAppointment = () => {
  const { clinicId } = useParams();
  const id = clinicId ? parseInt(clinicId, 10) : null;

  const doctorData = getDoctorData();
  const [state, setState] = useState({
    verifyPatient: false,
    getAppointData: [],
    showPriscription: false,
    showAppointmentBook: false,
    getPatientInformation: null,
    getPatntPrsInf: null,
    selectClinicFromDropdwn: false,
    settleDuePayment: false,
    clickableClinicName: null,
    patientDetails: [],
    loadinSpinner: true,
    ClinicI: null,
    allPatient: true,
    editScheduleDetail: null,
    editSchedule: false,
    deleteScheduleDetail: null,
    deleteSchedule: false,
    getClinicDetails: [],
    paymentSettled: false,
    searchAppointment: [],
    searchTerm: "",
    lastAppointment: {},
    dueLeft: false,
    daysLeft: "",
    prescriptionPhotoURLs: [],
    planType: "BASIC",
    remainDays: 365,
  });

  const toggleState = (key, value) =>
    setState((prev) => ({ ...prev, [key]: value }));

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Load clinics from localStorage
  const loadClinics = useCallback(() => {
    const clinics = getClinics();
    const activeClinics = clinics.filter((c) => c.clinicStatus !== "DELETED");
    toggleState("getClinicDetails", activeClinics);
    if (activeClinics.length > 0) {
      toggleState("clickableClinicName", activeClinics[0]?.location || "Select Clinic");
    }
  }, []);

  // Load appointments based on clinic and date
  const loadAppointments = useCallback(
    (clinicIdParam, date) => {
      const allAppointments = getAppointments();
      let filtered = allAppointments;
      if (clinicIdParam) {
        filtered = filtered.filter((apt) => apt.clinicId === parseInt(clinicIdParam, 10));
      }
      if (date) {
        filtered = filtered.filter((apt) => apt.appointmentDate === date);
      }
      filtered.sort((a, b) => (a.appointmentTime || "").localeCompare(b.appointmentTime || ""));
      toggleState("getAppointData", filtered);
      toggleState("loadinSpinner", false);
      return filtered;
    },
    []
  );

  // Initial load
  useEffect(() => {
    loadClinics();
    const initialClinicId = id || getClinics()[0]?.id;
    if (initialClinicId) {
      toggleState("ClinicI", initialClinicId);
      loadAppointments(initialClinicId, selectedDate);
    }
    toggleState("loadinSpinner", false);
  }, []);

  // Handle clinic selection from dropdown
  const handleClinicModal = useCallback(
    (clinicIdParam) => {
      toggleState("ClinicI", clinicIdParam);
      toggleState("allPatient", false);
      toggleState("loadinSpinner", true);
      loadAppointments(clinicIdParam, selectedDate);
      const clinics = getClinics();
      const clinic = clinics.find((c) => c.id === parseInt(clinicIdParam, 10));
      toggleState("clickableClinicName", clinic?.location || "Select Clinic");
      toggleState("selectClinicFromDropdwn", false);
      toggleState("loadinSpinner", false);
    },
    [selectedDate, loadAppointments]
  );

  // Show all appointments for the selected date (across all clinics)
  const showAllPatient = useCallback(() => {
    toggleState("allPatient", true);
    toggleState("loadinSpinner", true);
    const allAppointments = getAppointments();
    const filtered = allAppointments.filter((apt) => apt.appointmentDate === selectedDate);
    filtered.sort((a, b) => (a.appointmentTime || "").localeCompare(b.appointmentTime || ""));
    toggleState("getAppointData", filtered);
    toggleState("selectClinicFromDropdwn", false);
    toggleState("loadinSpinner", false);
  }, [selectedDate]);

  // Handle date change
  const handleDateChange = useCallback(
    (event) => {
      const newDate = event.target.value;
      setSelectedDate(newDate);
      if (state.ClinicI) {
        loadAppointments(state.ClinicI, newDate);
      } else {
        const allAppointments = getAppointments();
        const filtered = allAppointments.filter((apt) => apt.appointmentDate === newDate);
        filtered.sort((a, b) => (a.appointmentTime || "").localeCompare(b.appointmentTime || ""));
        toggleState("getAppointData", filtered);
      }
    },
    [state.ClinicI, loadAppointments]
  );

  // Search functionality
  useEffect(() => {
    const searchTerm = state.searchTerm.toLowerCase();
    if (!searchTerm) {
      toggleState("searchAppointment", state.getAppointData);
      return;
    }
    const filtered = state.getAppointData.filter(
      (apt) =>
        (apt.name || "").toLowerCase().includes(searchTerm) ||
        (apt.contact || "").toLowerCase().includes(searchTerm)
    );
    toggleState("searchAppointment", filtered);
  }, [state.searchTerm, state.getAppointData]);

  // Toggle dropdown
  const handleSeelctClinicFromDropDown = useCallback(() => {
    toggleState("selectClinicFromDropdwn", !state.selectClinicFromDropdwn);
  }, [state.selectClinicFromDropdwn]);

  // Visit status toggle
  const handleVisited = useCallback(
    (event, appointmentId) => {
      const isChecked = event.target.checked;
      const appointments = getAppointments();
      const index = appointments.findIndex((apt) => apt.id === appointmentId);
      if (index !== -1) {
        appointments[index].appointmentvisitStatus = isChecked ? "VISITED" : "PENDING";
        saveAppointments(appointments);
        const updatedData = state.getAppointData.map((apt) =>
          apt.id === appointmentId
            ? { ...apt, appointmentvisitStatus: isChecked ? "VISITED" : "PENDING" }
            : apt
        );
        toggleState("getAppointData", updatedData);
        toast.success(isChecked ? "Patient marked as Visited" : "Visit status updated");
      }
    },
    [state.getAppointData]
  );

  // Modal open/close functions
  const handleAppointmentModal = useCallback(() => toggleState("verifyPatient", true), []);
  const handleClosePatientModal = useCallback(() => toggleState("verifyPatient", false), []);

  const handlePriscriptionModal = useCallback(() => toggleState("showPriscription", true), []);
  const closePriscrptnModal = useCallback(() => toggleState("showPriscription", false), []);

  const handleShowPatientAppointment = useCallback(() => toggleState("showAppointmentBook", true), []);
  const closeShowPatientAppointment = useCallback(() => toggleState("showAppointmentBook", false), []);

  const handleSettleDuePayment = useCallback(() => toggleState("settleDuePayment", true), []);
  const closeSettleDuePayment = useCallback(() => toggleState("settleDuePayment", false), []);

  const settlePaymentHandler = useCallback(() => toggleState("paymentSettled", true), []);
  const closeHandleSettleDuePayment = useCallback(() => toggleState("paymentSettled", false), []);

  const isDentist = doctorData?.specialization?.[0]?.toLowerCase() === "dentist";

  return (
    <>
      <NavbarWrapper showNavbar={true} />

      <div className="bg-white border-gray-200 py-2.5 dark:bg-white">
        <div className="flex flex-wrap md:mt-20 mt-0 items-center justify-between max-w-screen-xl px-4 mx-auto">
          <p className="text-2xl mb-4 font-bold text-gray-900 montserrat">
            Your Appointment
          </p>
          <div className="flex items-center lg:order-2">
            <button
              onClick={handleAppointmentModal}
              className="text-white bg-purple-700 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-4 lg:px-5 py-2 lg:py-2.5 sm:mr-2 lg:mr-0 focus:outline-none dark:focus:ring-purple-800"
              style={{ backgroundColor: "rgb(4,106,110)" }}
            >
              Book Appointment
            </button>
          </div>

          <div
            className="items-center justify-between w-full lg:flex lg:w-auto lg:order-1"
            id="mobile-menu-2"
          >
            <ul className="flex flex-col mt-4 font-medium lg:flex-row lg:space-x-8 lg:mt-0">
              <form className="flex flex-col md:flex-row gap-3">
                <div className="flex">
                  <input
                    type="text"
                    placeholder="Patient Search"
                    value={state.searchTerm}
                    onChange={(e) => toggleState("searchTerm", e.target.value)}
                    className="w-full md:w-80 px-3 h-10 rounded-l border-2 border-gray-500 focus:outline-none focus:border-sky-500"
                  />
                  <button
                    type="submit"
                    className="text-white rounded-r px-2 md:px-3 py-0 md:py-1"
                    style={{ backgroundColor: "rgb(4,106,110)" }}
                    onClick={(e) => e.preventDefault()}
                  >
                    Search
                  </button>
                </div>
              </form>
            </ul>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between max-w-screen-xl px-4 pt-6 mx-auto">
          <div className="flex mb-4 lg:mb-0">
            <p className="mr-2 lg:mr-4">Choose Date:</p>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="mb-2 lg:mb-0"
            />
          </div>

          <div className="flex items-center">
            <p className="pr-3">Show</p>
            <div className="relative ml-2">
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={showAllPatient}
                  className={`px-3 ${
                    state.allPatient ? "text-amber-800 underline" : ""
                  } py-2 text-amber-500`}
                  style={{ backgroundColor: "rgb(255,245,235)" }}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={handleSeelctClinicFromDropDown}
                  className={`flex ${
                    !state.allPatient ? "text-amber-800 underline" : ""
                  } text-amber-500 items-center justify-center rounded-lg px-4 py-2 bg-purple-700`}
                  style={{ backgroundColor: "rgb(255,245,235)" }}
                >
                  <span>
                    {state.clickableClinicName === null || state.allPatient
                      ? "Select Clinic"
                      : state.clickableClinicName.substring(0, 12)}
                  </span>
                  <svg
                    className="w-5 h-5 ml-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 12.586l-4.293-4.293a1 1 0 0 1 1.414-1.414L10 10.758l3.879-3.879a1 1 0 1 1 1.414 1.414L10 12.586z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              {state.selectClinicFromDropdwn && (
                <div className="absolute right-3 mt-2 w-50 bg-white rounded-lg shadow-lg">
                  {state.getClinicDetails.map((clinic) => (
                    <button
                      key={clinic.id}
                      className="block w-full px-4 py-2 text-md montserrat text-gray-700 hover:bg-gray-100"
                      onClick={() => handleClinicModal(clinic.id)}
                    >
                      {clinic.location?.substring(0, 12) || "Unknown"}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 overflow-x-auto">
          <thead className="bg-gray-200">
            <tr>
              {[
                "Serial No.",
                "Visit Status",
                "Time Of Visit",
                "Name",
                "Age",
                "Status",
                "Address",
                "Guardian Name",
                "Action",
                "Edit",
              ].map((header, index) => (
                <th
                  key={index}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {!state.searchAppointment || state.searchAppointment.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-8">
                  <div className="text-gray-800 montserrat">
                    No appointment has been booked!
                  </div>
                </td>
              </tr>
            ) : (
              state.searchAppointment.map((patient, index) => (
                <tr key={patient.id || index}>
                  <td className="px-6 py-4 whitespace-nowrap">{index + 1}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={patient.appointmentvisitStatus === "VISITED"}
                      className="form-radio h-5 w-5 text-blue-600 cursor-pointer"
                      onChange={(event) => handleVisited(event, patient.id)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {patient.appointmentTime}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          <button
                            onClick={() => {
                              handleShowPatientAppointment();
                              toggleState("getPatientInformation", patient);
                            }}
                          >
                            {patient.name}
                          </button>
                          {state.showAppointmentBook && (
                            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                              <div className="bg-white p-6 rounded-lg max-w-md w-full">
                                <h2 className="text-xl font-bold mb-4">Appointment Details</h2>
                                <p><strong>Patient:</strong> {state.getPatientInformation?.name}</p>
                                <p><strong>Time:</strong> {state.getPatientInformation?.appointmentTime}</p>
                                <button
                                  onClick={closeShowPatientAppointment}
                                  className="mt-4 bg-teal-500 text-white px-4 py-2 rounded"
                                >
                                  Close
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{patient.age}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-sm leading-5 font-semibold rounded-full ${
                        patient.paymentStatus === "UNPAID"
                          ? "text-red-800"
                          : "text-green-500"
                      }`}
                    >
                      {patient.paymentStatus === "UNPAID" ? (
                        <button
                          type="button"
                          onClick={() => {
                            toggleState("patientDetails", patient);
                            handleSettleDuePayment();
                          }}
                        >
                          {patient.paymentStatus}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            toggleState("patientDetails", patient);
                            settlePaymentHandler();
                          }}
                        >
                          {patient.paymentStatus}
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {patient.address}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {patient.guardianName}
                  </td>
                  <td className="flex px-6 py-6 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        toggleState("getPatntPrsInf", patient);
                        handlePriscriptionModal();
                      }}
                    >
                      <img
                        className="h-8 w-8 rounded-full"
                        src={prescriptionBlack}
                        alt="Prescription"
                      />
                    </button>
                    {state.showPriscription && (
                      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                        <div className="bg-white p-6 rounded-lg max-w-md w-full">
                          <h2 className="text-xl font-bold mb-4">Prescription</h2>
                          <p><strong>Patient:</strong> {state.getPatntPrsInf?.name}</p>
                          <p><strong>Doctor:</strong> {doctorData.firstName} {doctorData.lastName}</p>
                          <button
                            onClick={closePriscrptnModal}
                            className="mt-4 bg-teal-500 text-white px-4 py-2 rounded"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}
                    <Link to={`/uploadDocument/${patient.patientId}`} state={{ patient }}>
                      <img
                        className="md:h-8 md:w-8 h-6 w-6 sm:pt-2 pt-0 md:p-1"
                        src={upload}
                        alt="Upload"
                      />
                    </Link>
                  </td>
                  <td className="px-4 py-6 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          toggleState("editScheduleDetail", patient);
                          toggleState("editSchedule", true);
                        }}
                      >
                        <svg className="w-6 h-6" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 19.0003H17M2.666 11.1873C2.23962 11.6147 2.00011 12.1937 2 12.7973V16.0003H5.223C5.827 16.0003 6.406 15.7603 6.833 15.3323L16.333 5.82734C16.7592 5.39993 16.9986 4.82095 16.9986 4.21734C16.9986 3.61372 16.7592 3.03474 16.333 2.60734L15.395 1.66734C15.1835 1.45569 14.9323 1.28781 14.6558 1.1733C14.3794 1.0588 14.083 0.999907 13.7838 1C13.4846 1.00009 13.1883 1.05917 12.9119 1.17385C12.6355 1.28852 12.3844 1.45656 12.173 1.66834L2.666 11.1873Z" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          toggleState("deleteScheduleDetail", patient);
                          toggleState("deleteSchedule", true);
                        }}
                      >
                        <svg className="w-7 h-7 text-red-500 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 2a1 1 0 011 1v1h6a1 1 0 110 2h-1v11a2 2 0 01-2 2H7a2 2 0 01-2-2V6H4a1 1 0 110-2h6V3a1 1 0 011-1zM7 6v11h6V6H7zm2-3v1h2V3H9z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ====== MODALS WITH REAL COMPONENTS ====== */}

      {state.verifyPatient && (
        <VerifyPatientModal
          reset={handleClosePatientModal}
          onSubmit={() => {}}
        />
      )}

      {state.editSchedule && state.editScheduleDetail && (
        <EditScheduleAppointment
          detail={state.editScheduleDetail}
          reset={() => {
            toggleState("editSchedule", false);
            // Refresh appointments after edit
            loadAppointments(state.ClinicI, selectedDate);
          }}
        />
      )}

      {state.deleteSchedule && state.deleteScheduleDetail && (
        <DeleteScheduleAppointment
          detail={state.deleteScheduleDetail}
          reset={() => {
            toggleState("deleteSchedule", false);
            // Refresh appointments after delete
            loadAppointments(state.ClinicI, selectedDate);
          }}
        />
      )}

      {state.settleDuePayment && state.patientDetails && (
        isDentist ? (
          <UnpaidtoPaid
            patientDetails={state.patientDetails}
            reset={() => {
              closeSettleDuePayment();
              loadAppointments(state.ClinicI, selectedDate);
            }}
          />
        ) : (
          <PaymentMode
            patientDetails={state.patientDetails}
            reset={() => {
              closeSettleDuePayment();
              loadAppointments(state.ClinicI, selectedDate);
            }}
          />
        )
      )}

      {state.paymentSettled && state.patientDetails && (
        <SettleDuePayment
          patientDetails={state.patientDetails}
          reset={() => {
            closeHandleSettleDuePayment();
            loadAppointments(state.ClinicI, selectedDate);
          }}
        />
      )}

      {state.dueLeft && (
        <DueModal
          reset={() => toggleState("dueLeft", false)}
          daysLeft={state.daysLeft}
        />
      )}

      <ToastContainer />
      {state.loadinSpinner && <Spinner />}
    </>
  );
};

export default ListOfAppointment;